/**
 * A file's bytes, read the moment the user picks or drops it (Android, 2026-10-11: the phone may stop
 * handing a file over once the picker's event has passed, e.g. a content:// file from another app).
 * Call `startReading` synchronously in the change/drop handler, before any other await. File.arrayBuffer()
 * first, else FileReader, and the whole sequence retried once; each method's failure is kept (its error
 * type only goes to the diagnostic report). The bytes stay in page memory; nothing is stored or sent.
 */
export type ReadMethod = 'arrayBuffer' | 'fileReader';

export interface BytesResult {
  bytes?: Uint8Array;
  /** The method that worked. */
  method?: ReadMethod;
  /** Each failed attempt, in order. */
  failures: { method: ReadMethod; error: unknown }[];
}

type Readable = Pick<Blob, 'arrayBuffer'>;

function viaFileReader(file: Blob, Reader: typeof FileReader | undefined): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    if (!Reader) return reject(Object.assign(new Error('no FileReader'), { name: 'NotSupportedError' }));
    const r = new Reader();
    r.onload = () => (r.result instanceof ArrayBuffer ? resolve(r.result) : reject(new TypeError('FileReader gave no ArrayBuffer')));
    r.onerror = () => reject(r.error ?? new Error('FileReader failed'));
    r.readAsArrayBuffer(file);
  });
}

export function startReading(file: Blob & Readable, Reader: typeof FileReader | undefined = typeof FileReader === 'undefined' ? undefined : FileReader): Promise<BytesResult> {
  const failures: BytesResult['failures'] = [];
  // Started now, synchronously: the first arrayBuffer() call happens inside the caller's event handler.
  const first = file.arrayBuffer();
  const attempt = async (pending?: Promise<ArrayBuffer>): Promise<BytesResult | undefined> => {
    try {
      return { bytes: new Uint8Array(await (pending ?? file.arrayBuffer())), method: 'arrayBuffer', failures };
    } catch (error) {
      failures.push({ method: 'arrayBuffer', error });
    }
    try {
      return { bytes: new Uint8Array(await viaFileReader(file, Reader)), method: 'fileReader', failures };
    } catch (error) {
      failures.push({ method: 'fileReader', error });
    }
    return undefined;
  };
  return (async () => (await attempt(first)) ?? (await attempt()) ?? { failures })();
}
